// Page <title>, description, Open Graph and Twitter card tags for a story.
// React 19 hoists these into <head>; social crawlers see them because
// production nginx routes bots through the prerender service.
//
// Values come from the story's `SEO` field, which exists in two plugin
// shapes: `seo_metatags` (title, description, og_*, twitter_*) on newer
// stories and `meta-fields` (title, description only) on older ones. Most
// stories leave it empty, so every value falls back to the story's own
// title / subtitle / summary / featured image.

const SITE_NAME = "Tiltify Blog";

const stripHtml = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").replace(/\s+/g, " ").trim();
};

const truncate = (text, maxLength) =>
  text.length <= maxLength ? text : `${text.slice(0, maxLength).trimEnd()}…`;

// Storyblok asset URLs can be protocol-relative ("//a.storyblok.com/...");
// crawlers need absolute https URLs. Resize uploads to a sensible share size
// (width only, so nothing is cropped).
const shareImageUrl = (url) => {
  if (!url) return "";
  const absolute = url.startsWith("//") ? `https:${url}` : url;
  return absolute.includes("a.storyblok.com") && !absolute.includes("/m/")
    ? `${absolute}/m/1200x0`
    : absolute;
};

// Storyblok datetimes look like "2026-07-06 00:00" (UTC); Open Graph wants
// ISO 8601.
const isoDate = (value) => {
  const date = new Date(`${String(value).replace(" ", "T")}Z`);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

const firstOf = (...values) => values.find((value) => typeof value === "string" && value.trim())?.trim() || "";

export const MetaTags = ({ story }) => {
  const content = story?.content;
  if (!content) return null;

  const seo = content.SEO || {};
  const isPost = content.component === "blogpost";

  const title = firstOf(seo.title, content.title, SITE_NAME);
  const description = truncate(
    firstOf(seo.description, content.subtitle, stripHtml(content.summary), content.subheading),
    300,
  );
  const ogTitle = firstOf(seo.og_title, title);
  const ogDescription = firstOf(seo.og_description, description);
  const ogImage = shareImageUrl(firstOf(seo.og_image, content.featured_image?.filename));
  const twitterTitle = firstOf(seo.twitter_title, ogTitle);
  const twitterDescription = firstOf(seo.twitter_description, ogDescription);
  const twitterImage = shareImageUrl(firstOf(seo.twitter_image, seo.og_image, content.featured_image?.filename));
  const url = window.location.origin + window.location.pathname;
  const publishedTime = isPost && content.published_at ? isoDate(content.published_at) : "";

  return (
    <>
      <title>{title}</title>
      <link rel="canonical" href={url} />
      {description ? <meta name="description" content={description} /> : null}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={isPost ? "article" : "website"} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={ogTitle} />
      {ogDescription ? <meta property="og:description" content={ogDescription} /> : null}
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      {publishedTime ? <meta property="article:published_time" content={publishedTime} /> : null}

      <meta name="twitter:card" content={twitterImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={twitterTitle} />
      {twitterDescription ? <meta name="twitter:description" content={twitterDescription} /> : null}
      {twitterImage ? <meta name="twitter:image" content={twitterImage} /> : null}
    </>
  );
};
